/**
 * Lightweight route guard for the admin portal.
 *
 * @remarks
 * We intentionally avoid introducing a heavyweight AuthProvider layer here. Instead we rely on the shared
 * `useAuthState()` hook and (when needed) check custom claims on the current user.
 *
 * Logging note: keep logs low-noise and avoid dumping full claims or PII to the console.
 */

import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import AdminAccessDeniedPanel from "./AdminAccessDeniedPanel";
import { useAdminClaimCheck } from "./hooks/useAdminClaimCheck";
import RouteGuardLoadingState from "./RouteGuardLoadingState";

import { useAuthState } from "@/auth/helloCashflowDomain/useAuthState";
import { adminUiCopy } from "@/copy/adminUiCopy";
import { useAdminLogout } from "@/hooks/useAdminLogout";
import memoizedAuth from "@/lib/firebase/MemoizedAuth";
import { isAdminE2eBypassEnabled } from "@/utils/featureFlags";
import { normalizeAdminFrontendPath } from "@/utils/navigation/normalizeAdminFrontendPath";

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "SimpleProtectedRoute",
);
const CLAIM_CHECK_TIMEOUT_MS = 12_000;
const ROUTE_GUARD_LOG_DEDUPE_WINDOW_MS = 1_000;
const ADMIN_REQUIRED_CLAIMS = ["admin"];
const NO_REQUIRED_CLAIMS: string[] = [];
// Dev StrictMode remounts can replay the same route-guard log back-to-back.
const recentRouteGuardEventTimestamps = new Map<string, number>();
const getRouteGuardLogTimestamp = () => Date.now();

interface SimpleProtectedRouteProps {
  children: React.ReactNode;
  requiredClaims?: string[];
  fallbackPath?: string;
  onDefinitiveClaimDenial?: (actorUid: string) => void;
}

export const buildLoginRedirectPath = (args: {
  fallbackPath: string;
  hash?: string;
  pathname: string;
  search?: string;
}): string => {
  const [pathWithQuery, hashFragment = ""] = args.fallbackPath.split("#", 2);
  const [basePath, existingQuery = ""] = pathWithQuery.split("?", 2);
  const params = new URLSearchParams(existingQuery);
  const normalizedReturnUrl = normalizeAdminFrontendPath(
    `${args.pathname}${args.search ?? ""}${args.hash ?? ""}`,
  );

  if (normalizedReturnUrl) {
    params.set("return_url", normalizedReturnUrl);
  }

  const queryString = params.toString();

  return `${basePath}${queryString ? `?${queryString}` : ""}${hashFragment ? `#${hashFragment}` : ""}`;
};

/**
 * Lightweight admin route guard for Firebase-authenticated pages.
 *
 * @remarks
 * This component is intentionally simple: it blocks rendering until Firebase auth
 * has resolved, redirects unauthenticated users to `fallbackPath`, and (when
 * `requiredClaims` includes `"admin"`) performs a custom-claim check.
 *
 * E2E bypass mode exists for local/test workflows and skips *all* auth/claim
 * enforcement here. Do not enable it outside controlled environments.
 *
 * @see apps/admin/src/components/auth/hooks/useAdminClaimCheck.ts
 * @see apps/admin/src/utils/featureFlags.ts
 * @see docs/LOGGING.md
 */
const SimpleProtectedRoute: React.FC<SimpleProtectedRouteProps> = ({
  children,
  requiredClaims = NO_REQUIRED_CLAIMS,
  fallbackPath = "/login",
  onDefinitiveClaimDenial,
}) => {
  const { user, loading } = useAuthState();
  const resolvedUser = user ?? memoizedAuth.getCurrentUser();
  const adminE2eBypassEnabled = isAdminE2eBypassEnabled();
  const location = useLocation();
  const canonicalRequiredClaims = useMemo(
    () =>
      requiredClaims.includes("admin")
        ? ADMIN_REQUIRED_CLAIMS
        : NO_REQUIRED_CLAIMS,
    [requiredClaims],
  );
  const requiredClaimsSignature = canonicalRequiredClaims.join("|");
  const loginRedirectPath = buildLoginRedirectPath({
    fallbackPath,
    hash: location.hash,
    pathname: location.pathname,
    search: location.search,
  });
  const { logout, isLoggingOut } = useAdminLogout();
  const claimCheckScopeKey = `${location.pathname}:${resolvedUser?.uid ?? "anonymous"}`;
  const [claimCheckRetryState, setClaimCheckRetryState] = useState<{
    attempt: number;
    scopeKey: string;
  }>({
    attempt: 0,
    scopeKey: "",
  });
  const [timedOutClaimCheckScopeKey, setTimedOutClaimCheckScopeKey] = useState<
    string | null
  >(null);
  const claimCheckAttempt =
    claimCheckRetryState.scopeKey === claimCheckScopeKey
      ? claimCheckRetryState.attempt
      : 0;
  const hasClaimCheckTimedOut =
    timedOutClaimCheckScopeKey === claimCheckScopeKey;
  const {
    claimCheckFailed,
    hasAdminClaim,
    isCheckingClaims,
    requiresAdminClaim,
  } = useAdminClaimCheck({
    adminE2eBypassEnabled,
    claimCheckAttempt,
    requiredClaims: canonicalRequiredClaims,
    user: resolvedUser,
  });
  const resolvedUserPresent = Boolean(resolvedUser);
  const userId = resolvedUser?.uid;

  const logRouteGuardEvent = useCallback(
    (
      level: "debug" | "info" | "notice" | "warn",
      message: string,
      metadata: Record<string, unknown>,
    ) => {
      const now = getRouteGuardLogTimestamp();
      for (const [
        cachedSignature,
        timestamp,
      ] of recentRouteGuardEventTimestamps) {
        if (now - timestamp > ROUTE_GUARD_LOG_DEDUPE_WINDOW_MS) {
          recentRouteGuardEventTimestamps.delete(cachedSignature);
        }
      }

      const signature = JSON.stringify({
        level,
        operation: metadata.operation ?? null,
        pathname: metadata.pathname ?? location.pathname,
        fallbackPath: metadata.fallbackPath ?? fallbackPath,
        requiredClaims: metadata.requiredClaims ?? requiredClaimsSignature,
      });

      const previousTimestamp = recentRouteGuardEventTimestamps.get(signature);
      if (
        typeof previousTimestamp === "number" &&
        now - previousTimestamp < ROUTE_GUARD_LOG_DEDUPE_WINDOW_MS
      ) {
        return;
      }

      recentRouteGuardEventTimestamps.set(signature, now);
      logger[level](message, metadata);
    },
    [fallbackPath, location.pathname, requiredClaimsSignature],
  );

  useEffect(() => {
    if (!requiresAdminClaim || !isCheckingClaims || hasClaimCheckTimedOut) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      logger.debug("Admin claim check timed out while route loading", {
        operation: "claims_check_timeout",
        pathname: location.pathname,
        requiredClaims: requiredClaimsSignature,
        fallbackPath,
        loginRedirectPath,
        resolvedUserPresent,
        userId,
        claimCheckScopeKey,
        claimCheckAttempt,
      });
      setTimedOutClaimCheckScopeKey(claimCheckScopeKey);
    }, CLAIM_CHECK_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    claimCheckScopeKey,
    claimCheckAttempt,
    hasClaimCheckTimedOut,
    isCheckingClaims,
    location.pathname,
    loginRedirectPath,
    fallbackPath,
    requiredClaimsSignature,
    requiresAdminClaim,
    resolvedUserPresent,
    userId,
  ]);

  const routeGuardState = useMemo(() => {
    if (adminE2eBypassEnabled) {
      return "bypass_enabled" as const;
    }

    if (loading) {
      return "auth_loading" as const;
    }

    if (!resolvedUserPresent) {
      return "redirect_login" as const;
    }

    if (requiresAdminClaim && isCheckingClaims && !hasClaimCheckTimedOut) {
      return "claims_check_hold" as const;
    }

    if (requiresAdminClaim && isCheckingClaims && hasClaimCheckTimedOut) {
      return "claims_check_timeout_recovery_state" as const;
    }

    if (requiresAdminClaim && claimCheckFailed) {
      return "claims_check_failed" as const;
    }

    if (requiresAdminClaim && !hasAdminClaim) {
      return "claims_mismatch_denied" as const;
    }

    return "pass_through" as const;
  }, [
    adminE2eBypassEnabled,
    claimCheckFailed,
    hasAdminClaim,
    hasClaimCheckTimedOut,
    isCheckingClaims,
    loading,
    requiresAdminClaim,
    resolvedUserPresent,
  ]);

  useEffect(() => {
    // CRITICAL: a confirmed claim refusal retires actor-bound drafts; failed
    // or pending checks remain recoverable uncertainty, not a refusal.
    // https://firebase.google.com/docs/auth/admin/custom-claims
    if (routeGuardState === "claims_mismatch_denied" && userId)
      onDefinitiveClaimDenial?.(userId);
  }, [onDefinitiveClaimDenial, routeGuardState, userId]);

  useEffect(() => {
    const metadata = {
      pathname: location.pathname,
      fallbackPath,
      loginRedirectPath,
      requiredClaims: requiredClaimsSignature,
      resolvedUserPresent,
      userId,
      adminE2eBypassEnabled,
      claimCheckScopeKey,
      claimCheckAttempt,
      requiresAdminClaim,
      isCheckingClaims,
      hasAdminClaim,
      claimCheckFailed,
      hasClaimCheckTimedOut,
    };

    // Route-guard logs live in an effect so same-state rerenders caused by URL typing or query
    // refreshes do not spam identical auth decisions while access semantics remain unchanged.
    switch (routeGuardState) {
      case "bypass_enabled":
        logRouteGuardEvent(
          "debug",
          "Admin E2E bypass enabled; skipping route guard",
          {
            operation: "route_guard_bypass_enabled",
            pathname: location.pathname,
            adminE2eBypassEnabled,
          },
        );
        return;
      case "auth_loading":
        logRouteGuardEvent(
          "debug",
          "Auth still loading; blocking route render",
          {
            operation: "auth_loading",
            ...metadata,
          },
        );
        return;
      case "redirect_login":
        logRouteGuardEvent("notice", "Route fallback navigation to login", {
          operation: "auth_fallback_navigation",
          ...metadata,
        });
        return;
      case "claims_check_hold":
        logRouteGuardEvent(
          "debug",
          "Admin claim check still in progress; holding route",
          {
            operation: "claims_check_hold",
            ...metadata,
          },
        );
        return;
      case "claims_check_timeout_recovery_state":
        logRouteGuardEvent(
          "warn",
          "Admin claim check timed out; route is showing recovery state",
          {
            operation: "claims_check_timeout_recovery_state",
            ...metadata,
          },
        );
        return;
      case "claims_check_failed":
        logRouteGuardEvent(
          "warn",
          "Admin claim check failed; route is showing retry state",
          {
            operation: "claims_check_failed_recovery_state",
            ...metadata,
          },
        );
        return;
      case "claims_mismatch_denied":
        logRouteGuardEvent("warn", "Route denied due to missing admin claim", {
          operation: "claims_mismatch_denied",
          ...metadata,
        });
        return;
      case "pass_through":
        logRouteGuardEvent("info", "Route guard pass-through granted", {
          operation: "route_guard_pass_through",
          ...metadata,
        });
        return;
    }
  }, [
    adminE2eBypassEnabled,
    claimCheckAttempt,
    claimCheckFailed,
    claimCheckScopeKey,
    fallbackPath,
    hasAdminClaim,
    hasClaimCheckTimedOut,
    isCheckingClaims,
    location.pathname,
    loginRedirectPath,
    logRouteGuardEvent,
    requiredClaimsSignature,
    requiresAdminClaim,
    resolvedUserPresent,
    routeGuardState,
    userId,
  ]);

  if (adminE2eBypassEnabled) {
    return <>{children}</>;
  }

  // CRITICAL: Block rendering while auth is loading to prevent "flash of redirect" and
  // to avoid running route-level side effects before we know if a user is present.
  if (loading) {
    return <RouteGuardLoadingState message={adminUiCopy.loading.adminAccess} />;
  }

  if (!resolvedUser) {
    return (
      <Navigate to={loginRedirectPath} state={{ from: location }} replace />
    );
  }

  // Show loading while checking claims
  if (requiresAdminClaim && isCheckingClaims && !hasClaimCheckTimedOut) {
    return (
      <RouteGuardLoadingState message={adminUiCopy.loading.adminAccessCheck} />
    );
  }

  if (requiresAdminClaim && isCheckingClaims && hasClaimCheckTimedOut) {
    return (
      <Box
        sx={{
          alignItems: "center",
          bgcolor: "background.default",
          display: "flex",
          justifyContent: "center",
          minHeight: "100vh",
          p: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderColor: "divider",
            borderRadius: 2,
            borderStyle: "solid",
            borderWidth: 1,
            maxWidth: 640,
            p: 4,
            width: "100%",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {adminUiCopy.authAccess.claimCheckTimedOutTitle}
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            {adminUiCopy.authAccess.claimCheckTimedOutMessage}
          </Alert>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => {
                setTimedOutClaimCheckScopeKey(null);
                setClaimCheckRetryState((current) => ({
                  attempt:
                    current.scopeKey === claimCheckScopeKey
                      ? current.attempt + 1
                      : 1,
                  scopeKey: claimCheckScopeKey,
                }));
              }}
            >
              {adminUiCopy.routeAccess.retryChecks}
            </Button>
            <Button
              variant="text"
              disabled={isLoggingOut}
              onClick={() => {
                void logout();
              }}
            >
              {adminUiCopy.common.signOut}
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (requiresAdminClaim && claimCheckFailed) {
    return (
      <Box
        sx={{
          alignItems: "center",
          bgcolor: "background.default",
          display: "flex",
          justifyContent: "center",
          minHeight: "100vh",
          p: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderColor: "divider",
            borderRadius: 2,
            borderStyle: "solid",
            borderWidth: 1,
            maxWidth: 640,
            p: 4,
            width: "100%",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {adminUiCopy.authAccess.claimCheckFailedTitle}
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            {adminUiCopy.authAccess.claimCheckFailedMessage}
          </Alert>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => {
                setTimedOutClaimCheckScopeKey(null);
                setClaimCheckRetryState((current) => ({
                  attempt:
                    current.scopeKey === claimCheckScopeKey
                      ? current.attempt + 1
                      : 1,
                  scopeKey: claimCheckScopeKey,
                }));
              }}
            >
              {adminUiCopy.routeAccess.retryChecks}
            </Button>
            <Button
              variant="text"
              disabled={isLoggingOut}
              onClick={() => {
                void logout();
              }}
            >
              {adminUiCopy.common.signOut}
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Check admin claims if required
  if (requiresAdminClaim && !hasAdminClaim) {
    return (
      <AdminAccessDeniedPanel
        isLoggingOut={isLoggingOut}
        onLogout={() => void logout()}
        user={resolvedUser}
      />
    );
  }

  // User is authenticated and authorized
  return <>{children}</>;
};

export default SimpleProtectedRoute;
